import { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { ConnectBank } from '~/components/modules/Deposit/ConnectBank';
import { ConnectSourceOption } from '~/components/modules/Deposit/ConnectSourceOption';
import { SummaryGrid } from '~/components/modules/SummaryTable/SummaryGrid';
import { AnalyticsEventNames } from '~/constants/AnalyticsEventMap';
import { DepositContext } from '~/context/Deposit/DepositContext';
import { usePlaidLinkToken } from '~/hooks/payments/usePlaidLinkToken';
import { useTracking } from '~/hooks/tracking/useTracking';
import { ModalMultiStepProps } from '~/models/deposits/ModalMultiStepProps';
import { baseRoutes } from '~/routes/base';
import { LoadingSkeleton, Placeholder } from '~/vinovest-design-system/components';
import { Alert } from '~/vinovest-design-system/components/Form/components/Alert';
import {
    IconBank,
    IconChevron,
    IconCreditCard,
    IconCrypto,
    IconDomestic,
    IconGlobe
} from '~/vinovest-design-system/components/Icons';

interface SelectSourceProps extends ModalMultiStepProps {
    setIsPlaidComplete: (status: boolean) => void;
    onPaymentLinked: () => void;
}

const BankAccountSkeleton = () => (
    <div className={'flex w-full justify-between group items-center py-4 px-4 sm:px-6'}>
        <div className={'flex flex-row'}>
            <div className={'bg-platinum-800 p-3 rounded-full mr-4'}>
                <IconBank className={'h-5 w-5 stroke-blue-300'} />
            </div>
            <div className={''}>
                <LoadingSkeleton width={'85px'} height={'10px'} classnames={'mb-3 mt-1'} />
                <LoadingSkeleton width={'100px'} height={'10px'} classnames={'mb-0'} />
            </div>
        </div>
        <IconChevron
            width={'15px'}
            height={'10px'}
            className={'opacity-20 -rotate-90 group-hover:translate-x-2 transition-all'}
            stroke={'#242E35'}
        />
    </div>
);

/** Displays all options to connect an account or transfer funds from. */
export const SelectSource = ({ setIsPlaidComplete, setStep, onPaymentLinked }: SelectSourceProps) => {
    const { captureEvent } = useTracking();
    const router = useRouter();

    const { destinationPortfolioId } = useContext(DepositContext);
    const { data: token } = usePlaidLinkToken(destinationPortfolioId);
    const [expandOptions, setExpandOptions] = useState(false);
    const [allowExpand, setAllowExpand] = useState(true);
    const [error, setError] = useState<string | undefined>();
    const [savingBankAccount, setSavingBankAccount] = useState(false);
    const sourceHeight = expandOptions ? 'h-[475px]' : 'h-[150px]';

    useEffect(() => {
        // dirty hack to disable for the upsell pages.
        setAllowExpand(!router.pathname.startsWith(baseRoutes.upsell.href));
    }, [router, setAllowExpand]);

    return (
        <>
            {savingBankAccount && (
                <Alert type={'success'} classnames={'animate-pulse mb-4'}>
                    Saving bank account for future payments ....
                </Alert>
            )}
            {error && (
                <Alert type={'error'} classnames={'mb-4'}>
                    {error}
                </Alert>
            )}

            <SummaryGrid classnames={`border-y transition-all overflow-hidden ease-in-out ${sourceHeight}`}>
                <Placeholder renderCondition={!!token} placeholder={<BankAccountSkeleton />}>
                    {token && (
                        <ConnectBank
                            token={token}
                            setIsPlaidComplete={setIsPlaidComplete}
                            onPaymentLinked={onPaymentLinked}
                            setLoading={setSavingBankAccount}
                            setError={setError}
                        />
                    )}
                </Placeholder>
                <ConnectSourceOption
                    title={'Credit Card'}
                    subtitle={'Instant'}
                    icon={<IconCreditCard className={'h-5 w-5 stroke-brown-300'} />}
                    onClick={() => {
                        setStep(2);
                        captureEvent(AnalyticsEventNames.SelectPaymentMethod, { paymentMethod: 'credit_card' });
                    }}
                />
                <ConnectSourceOption
                    title={'Crypto Currency'}
                    subtitle={'Instant'}
                    icon={<IconCrypto className={'h-5 w-5 stroke-brown-300'} />}
                    onClick={() => {
                        setStep(3);
                        captureEvent(AnalyticsEventNames.SelectPaymentMethod, { paymentMethod: 'cryptocurrency' });
                    }}
                />
                <ConnectSourceOption
                    title={'US Wire Transfer'}
                    subtitle={'1 Business Day'}
                    icon={<IconDomestic className={'h-5 w-5 stroke-brown-300'} />}
                    onClick={() => {
                        setStep(4);
                        captureEvent(AnalyticsEventNames.SelectPaymentMethod, { paymentMethod: 'wire_domestic' });
                    }}
                />
                <ConnectSourceOption
                    title={'International Wire Transfer'}
                    subtitle={'1-3 Business Day(s)'}
                    icon={<IconGlobe className={'h-5 w-5 stroke-brown-300'} />}
                    onClick={() => {
                        setStep(5);
                        captureEvent(AnalyticsEventNames.SelectPaymentMethod, { paymentMethod: 'wire_international' });
                    }}
                />
            </SummaryGrid>

            <Placeholder renderCondition={!expandOptions && allowExpand} placeholder={null}>
                <button
                    type={'button'}
                    onClick={() => setExpandOptions(true)}
                    className={'text-12 text-brown-600 pt-6 w-full text-left'}
                >
                    More options
                </button>
            </Placeholder>
        </>
    );
};
